<script setup>
import {
  InteractionStatus2Enum,
  interactionStatusDropDownOptions,
  interactionStatusLabels,
} from "@/utils/constants.js";
import { computed } from "vue";

const props = defineProps({
  status: {
    type: String,
    default: null,
  },
  allowToOpen: {
    type: Boolean,
    default: true,
  },
  paddingSize: {
    type: String,
    default: "normal",
  },
  options: {
    type: Object,
    default: interactionStatusDropDownOptions,
  },
  interactionDetails: {
    type: Object,
    default: {},
  },
});

const emit = defineEmits(["updated"]);

const dropdownRef = ref();

const selected = ref(props.status);
const isModalVisible = ref(false);

watch(
  () => props.status,
  (val) => {
    selected.value = val;
  }
);

const themeByStatus = {
  [InteractionStatus2Enum.TICKET]: "red",
  [InteractionStatus2Enum.ONGOING]: "green", // TODO SCRUM1-1611 was gray, set green
  [InteractionStatus2Enum.TICKET_ON_HOLD]: "gray",
  [InteractionStatus2Enum.SOFT_ALERT]: "yellow",
  [InteractionStatus2Enum.TICKET_CLOSED]: "green",
  [InteractionStatus2Enum.VA_CLOSED]: "green",
};

const onStatusClick = (option) => {
  if (option.value !== props.status) {
    emit("updated", option.value);
  }

  isModalVisible.value = false;
};

const statusTheme = computed(() => {
  return themeByStatus[props.status];
});
</script>

<template>
  <dropdown
    ref="dropdownRef"
    class="status-dropdown"
    :class="{ [statusTheme]: true, 'is-visible': isModalVisible }"
    :label="interactionStatusLabels[selected]"
    header="Status"
    :show-header="false"
    v-model="selected"
    :options="Object.values(options)"
    :allowToOpen="allowToOpen"
    :padding-size="paddingSize"
    :showSearch="false"
    v-model:visibility="isModalVisible"
  >
    <template #pre-label-icon>
      <IconCircleFill class="label-icon" />
    </template>

    <div v-for="option in dropdownRef?.sortedOptions">
      <div
        class="dropdown-item d-flex align-items-center"
        :class="{ active: selected === option.value }"
        @click.stop="onStatusClick(option)"
      >
        <IconCircleFill :class="themeByStatus[option.value]" />
        <div
          class="d-flex align-items-center justify-content-between flex-grow-1"
        >
          <span class="option-label">{{ option.text }}</span>

          <IconCheck v-if="selected === option.value" />
        </div>
      </div>
    </div>
  </dropdown>
</template>

<style lang="scss" scoped>
.status-dropdown {
  :deep(.dropdown-menu) {
    width: 200px;

    .dropdown-item {
      .circle-icon {
        &.red {
          border: $red-200;
          fill: $red-200;
        }

        &.green {
          fill: $green-150;
        }

        &.yellow {
          fill: $yellow-250;
        }

        &.blue {
          fill: var(--primary-250);
        }

        &.gray {
          fill: $neutral-250;
        }
      }
    }
  }

  .option-label {
    margin-left: 0.75rem;
  }

  &.is-visible {
    &.red {
      :deep(.btn) {
        background-color: $red-50;
        border: 1px solid $red-100;
      }
    }

    &.gray {
      :deep(.btn) {
        background-color: $neutral-150;
        border: 1px solid $neutral-300;
      }
    }

    &.green {
      :deep(.btn) {
        background-color: $neutral-150;
        border: 1px solid $neutral-300;
      }
    }

    &.yellow {
      :deep(.btn) {
        background-color: $yellow-50;
        border: 1px solid $yellow-150;
      }
    }

    &.blue {
      :deep(.btn) {
        background-color: var(--primary-50);
        border: 1px solid var(--primary-150);
      }
    }
  }

  &.red {
    :deep(.btn) {
      background-color: $red-0;
      border: 1px solid $red-50;

      &:hover {
        border: 1px solid $red-100;
      }
      &.show {
        background-color: $red-50;
        border: 1px solid $red-150;
      }

      .label-icon {
        border: $red-200;
        fill: $red-200;
      }
    }
  }

  &.green {
    :deep(.btn) {
      background-color: $green-0;
      border: 1px solid $green-50;

      &:hover {
        border: 1px solid $green-100;
      }
      &.show {
        background-color: $green-50;
        border: 1px solid $green-150;
      }
      .label-icon {
        fill: $green-150;
      }
    }
  }

  &.yellow {
    :deep(.btn) {
      background-color: $yellow-0;
      border: 1px solid $yellow-50;
      &:hover {
        border: 1px solid $yellow-100;
      }
      &.show {
        background-color: $yellow-50;
        border: 1px solid $yellow-150;
      }
      .label-icon {
        fill: $yellow-250;
      }
    }
  }

  &.blue {
    :deep(.btn) {
      background-color: var(--primary-0);
      border: 1px solid var(--primary-50);
      &:hover {
        border: 1px solid var(--primary-100);
      }
      &.show {
        background-color: var(--primary-50);
        border: 1px solid var(--primary-150);
      }
      .label-icon {
        fill: var(--primary-250);
      }
    }
  }

  &.gray {
    :deep(.btn) {
      background-color: $neutral-50;
      border: 1px solid $neutral-150;
      &:hover {
        border: 1px solid $neutral-150;
      }
      &.show {
        background-color: $neutral-100;
        border: 1px solid $neutral-250;
      }
      .label-icon {
        fill: $neutral-250;
      }
    }
  }

  &.active {
    background: var(--primary-0);
    border: 1px solid var(--primary-50);
  }
}
</style>
